import { useTranslation } from 'react-i18next';

import { KPILabel, KPIValue, ConditionalKPIValue } from '@/components/kpi/kpi';
import { Flexbox } from '@/ui/flexbox/flexbox';
import { SubHeaderBold } from '@/ui/typography/widgets';
import { PERMISSION } from '@/user/permissions/permission.type';
import { useUserPermissions } from '@/user/permissions/use-user-permissions';

import { useProjectKpi } from './use-projects-kpi.query';
import { usePendingProjectRequestsCount } from '../projects-list/use-pending-project-requests-count.query';
import { KpiLoadingSkeleton } from '@/components/kpi/kpi-loading-skeleton';

type KpiItemProps = {
  label: string;
  value: number;
  useConditionalColor?: boolean;
};

const KpiItem = ({
  label,
  value,
  useConditionalColor = false,
}: KpiItemProps) => (
  <Flexbox name="kpi" alignItems="center" gap="12px">
    <KPILabel>{label}</KPILabel>
    {useConditionalColor ? (
      <ConditionalKPIValue condition={value >= 1}>{value}</ConditionalKPIValue>
    ) : (
      <KPIValue>{value}</KPIValue>
    )}
  </Flexbox>
);

export const ProjectsKPI = () => {
  const { t } = useTranslation('projects', { keyPrefix: 'kpi' });
  const { hasRequiredPermission } = useUserPermissions();
  const canViewPendingRequests = hasRequiredPermission(
    PERMISSION.VIEW_PENDING_PROJECT_REQUESTS,
  );
  const { data: kpis, isFetched } = useProjectKpi();
  const { data: pendingRequestsCountData, isFetched: isCountFetched } =
    usePendingProjectRequestsCount({
      enabled: canViewPendingRequests,
    });

  if (!isFetched || (canViewPendingRequests && !isCountFetched)) {
    return <KpiLoadingSkeleton />;
  }

  const pendingProjectsCount = pendingRequestsCountData?.count ?? 0;

  const baseKpiItems: KpiItemProps[] = [
    { label: t('total'), value: kpis?.total ?? 0 },
    { label: t('addedThisMonth'), value: kpis?.addedThisMonth ?? 0 },
    { label: t('dueDiligence'), value: kpis?.dueDiligence ?? 0 },
    { label: t('pilot'), value: kpis?.pilot ?? 0 },
  ];

  const pendingProjectsKpi: KpiItemProps = {
    label: t('pendingProjects'),
    value: pendingProjectsCount,
    useConditionalColor: true,
  };

  const kpiItems: KpiItemProps[] = [
    baseKpiItems[0],
    ...(canViewPendingRequests ? [pendingProjectsKpi] : []),
    ...baseKpiItems.slice(1),
  ];

  return (
    <Flexbox name="projects-kpi" gap="92px" fullWidth>
      <SubHeaderBold>{t`title`}</SubHeaderBold>
      {kpiItems.map((item, index) => (
        <KpiItem key={index} {...item} />
      ))}
    </Flexbox>
  );
};
